import type {
  BufferEntry,
  BufferFlushReason,
  BufferSnapshot,
  NormalizedBufferConfig,
} from '../../contracts/buffer';
import { NON_COMPRESSIBLE_SIGNAL_TYPES } from '../../contracts/buffer';
import type {
  BehaviorSignal,
  BehaviorSignalType,
} from '../../contracts/signals';

const NON_COMPRESSIBLE_SET = new Set<BehaviorSignalType>(
  NON_COMPRESSIBLE_SIGNAL_TYPES
);

/**
 * Buffer interno de agregação de sinais (Etapa 20).
 *
 * NÃO é a Transport Queue: sem persistência, retry, batch HTTP ou sendBeacon.
 * Aplica drop strategies em overflow e faz flush por tamanho/idade/hidden/destroy.
 * O flush é idempotente (drena as entries) e nunca chama Transport.
 */
export class BehaviorBuffer {
  private readonly config: NormalizedBufferConfig;

  private entries: BufferEntry[] = [];

  private flushCount = 0;

  private dropCount = 0;

  private compressedCount = 0;

  private lastFlushAt: number | undefined;

  private lastFlushReason: BufferFlushReason | undefined;

  constructor(config: NormalizedBufferConfig) {
    this.config = config;
  }

  reset(): void {
    this.entries = [];
    this.flushCount = 0;
    this.dropCount = 0;
    this.compressedCount = 0;
    this.lastFlushAt = undefined;
    this.lastFlushReason = undefined;
  }

  size(): number {
    return this.entries.length;
  }

  /** Adiciona um sinal, aplicando a drop strategy quando em overflow. */
  add(signal: BehaviorSignal, now: number): void {
    if (this.entries.length < this.config.maxSize) {
      this.entries.push({ signal, bufferedAt: now, count: 1 });
      return;
    }

    switch (this.config.dropStrategy) {
      case 'drop_newest':
        this.dropCount += 1;
        return;
      case 'drop_oldest':
        this.entries.shift();
        this.dropCount += 1;
        this.entries.push({ signal, bufferedAt: now, count: 1 });
        return;
      case 'compress_similar':
      default:
        this.addCompressed(signal, now);
        return;
    }
  }

  private addCompressed(signal: BehaviorSignal, now: number): void {
    if (!NON_COMPRESSIBLE_SET.has(signal.type)) {
      // Compacta no entry mais recente de mesmo tipo compactável (preserva
      // causalidade: não reordena, apenas incrementa a contagem).
      for (let i = this.entries.length - 1; i >= 0; i -= 1) {
        const entry = this.entries[i];
        if (entry && entry.signal.type === signal.type) {
          this.entries[i] = {
            signal,
            bufferedAt: now,
            count: entry.count + 1,
          };
          this.compressedCount += 1;
          return;
        }
      }
    }

    // Não compactável (crítico) ou sem par similar: abre espaço descartando o
    // entry compactável mais antigo; se só houver críticos, descarta o mais antigo.
    const dropIndex = this.findOldestCompressibleIndex();
    if (dropIndex >= 0) {
      this.entries.splice(dropIndex, 1);
    } else {
      this.entries.shift();
    }
    this.dropCount += 1;
    this.entries.push({ signal, bufferedAt: now, count: 1 });
  }

  private findOldestCompressibleIndex(): number {
    for (let i = 0; i < this.entries.length; i += 1) {
      const entry = this.entries[i];
      if (entry && !NON_COMPRESSIBLE_SET.has(entry.signal.type)) {
        return i;
      }
    }
    return -1;
  }

  shouldFlushBySize(): boolean {
    return this.entries.length >= this.config.flushSize;
  }

  hasAgedEntries(now: number): boolean {
    if (this.config.maxAgeMs <= 0) return false;
    const oldest = this.entries[0];
    if (!oldest) return false;
    return now - oldest.bufferedAt >= this.config.maxAgeMs;
  }

  /** Drena as entries do buffer. Idempotente: buffer vazio retorna lista vazia. */
  flush(reason: BufferFlushReason, now: number): readonly BufferEntry[] {
    const drained = this.entries;
    this.entries = [];
    this.flushCount += 1;
    this.lastFlushAt = now;
    this.lastFlushReason = reason;
    return drained;
  }

  getSnapshot(): BufferSnapshot {
    return {
      enabled: this.config.enabled,
      size: this.entries.length,
      maxSize: this.config.maxSize,
      flushSize: this.config.flushSize,
      maxAgeMs: this.config.maxAgeMs,
      dropStrategy: this.config.dropStrategy,
      flushCount: this.flushCount,
      dropCount: this.dropCount,
      compressedCount: this.compressedCount,
      lastFlushAt: this.lastFlushAt,
      lastFlushReason: this.lastFlushReason,
    };
  }
}
